import { useEffect } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { PageHero, CardsGrid, StepsRow, SplitFeature, CheckList, CtaBand, SectionHead } from "@/components/blocks";
import { LeadForm } from "@/components/LeadForm";
import { Reveal } from "@/components/Reveal";
import { COMPANY } from "@/data/site";
import { track } from "@/lib/analytics";
import { breadcrumbJsonLd } from "@/lib/seo";

const Block = ({ block }) => {
  switch (block.type) {
    case "cards":
      return (
        <section className="section-pad !pb-0">
          <div className="container-site">
            <CardsGrid items={block.items} heading={block.heading} sub={block.sub} cols={block.cols || 3} />
          </div>
        </section>
      );
    case "steps":
      return (
        <section className="section-pad !pb-0">
          <div className="container-site">
            <div className="rounded-xl bg-cts-soft border border-cts-line p-7 md:p-10">
              <StepsRow steps={block.steps} heading={block.heading} />
            </div>
          </div>
        </section>
      );
    case "split":
      return (
        <section className="section-pad !pb-0">
          <div className="container-site">
            <SplitFeature {...block} />
          </div>
        </section>
      );
    case "consider":
      return (
        <section className="section-pad !pb-0">
          <div className="container-site">
            <SectionHead eyebrow={block.eyebrow || "MAKE THE RIGHT CHOICE"} title={block.heading} />
            <Reveal>
              <CheckList items={block.items} />
            </Reveal>
          </div>
        </section>
      );
    case "text":
      return (
        <section className="section-pad !pb-0">
          <div className="container-site max-w-3xl">
            {block.heading && <h2 className="h2-display mb-5">{block.heading}</h2>}
            {(block.paragraphs || []).map((p) => (
              <p key={p.slice(0, 32)} className="lead-text mb-4">{p}</p>
            ))}
          </div>
        </section>
      );
    case "note":
      return (
        <section className="section-pad !pb-0">
          <div className="container-site">
            <div className="rounded-xl bg-cts-soft border border-cts-line p-6 md:p-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
              <p className="text-cts-charcoal text-[15px] leading-relaxed">{block.text}</p>
              {block.link && (
                <Link to={block.link.to} data-testid="product-service-crosslink" className="btn-outline shrink-0 whitespace-nowrap">
                  {block.link.label}
                </Link>
              )}
            </div>
          </div>
        </section>
      );
    default:
      return null;
  }
};

export default function DetailPage({ config }) {
  useEffect(() => {
    if (config.trackEvent) track(config.trackEvent, { page: config.path });
  }, [config]);

  const crumbs = [config.parent, { label: config.h1 }];

  return (
    <>
      <Seo title={config.metaTitle} siteName={COMPANY.brand} description={config.metaDesc} jsonLd={breadcrumbJsonLd(crumbs)} />
      <PageHero
        eyebrow={config.eyebrow}
        title={config.h1}
        intro={config.intro}
        crumbs={crumbs}
        image={config.heroImage}
        imgKey={config.heroImgKey}
        alt={config.heroAlt}
      />
      {(config.blocks || []).map((block, i) => (
        <Block key={i} block={block} />
      ))}
      {config.form && (
        <section id="enquiry" className="section-pad">
          <div className="container-site">
            <div className="max-w-xl mx-auto rounded-xl border border-cts-line bg-white shadow-[0_24px_60px_-40px_rgba(23,27,30,0.35)] p-6 md:p-9">
              <p className="eyebrow mb-2">ENQUIRE</p>
              <h2 className="h3-display mb-2">{config.form.title}</h2>
              {config.form.sub && <p className="text-cts-charcoal text-[15px] mb-6">{config.form.sub}</p>}
              <LeadForm
                formType={config.form.formType}
                requirement={config.form.requirement}
                fields={config.form.fields}
                customFields={config.form.customFields || []}
                submitLabel={config.form.submitLabel}
                trackEvent={config.form.trackEvent}
              />
            </div>
          </div>
        </section>
      )}
      {!config.form && <div className="section-pad" />}
      {config.cta && <CtaBand {...config.cta} />}
    </>
  );
}
